// people.tsx · v12.10.9.3 (#1306 Avatar shows the person's picture when there is one) · 2026-10-07
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// people.tsx - shared pieces for people and their user groups: name/identity helpers, "who must use a second step",
// the 2-step chip, avatar, the multi-group picker (no escalation - §36) and the read-only group chips.
// Two layers (S12/S15/S17): a person is in one or more groups; the most generous level from any of them wins;
// nothing is ever set on the person.
import type { Grants, Group, Policy, UserRow } from '../../lib/securityApi';
import { Ic, fmtDate, initials, plural } from './ui';

export const STD_NAMES: Record<string, string> = {
  owner: 'Owner', office: 'Office manager', front: 'Front desk', bill: 'Biller',
  team: 'Team member', author: 'Author', tech: 'Tech admin', sysadmin: 'System admin',
};

export function isInvite(u: UserRow): boolean { return u.status === 'invited' || u.status === 'expired'; }
export function isLocked(u: UserRow, now = Date.now()): boolean { return !!u.lockedUntil && new Date(u.lockedUntil).getTime() > now; }
export function lockMinsLeft(u: UserRow, now = Date.now()): number {
  return u.lockedUntil ? Math.max(1, Math.ceil((new Date(u.lockedUntil).getTime() - now) / 60000)) : 0;
}
export function identity(u: Pick<UserRow, 'email' | 'mobile'>): string { return u.email || u.mobile || ''; }
export function displayName(u: UserRow): string {
  const n = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  // v12.10.9.4 (#1339 F5): no name -> the whole part before @ (the full email shows on the line under it).
  return n || u.name || (u.email ? u.email.split('@')[0] : '') || u.mobile || 'Unnamed person';
}
export function methodName(k: string): string {
  return k === 'sms' ? 'Text message code' : k === 'totp' ? 'Authenticator app' : k === 'backup' ? 'Backup codes' : k;
}

export function sortGroups(groups: Group[]): Group[] { return groups.slice().sort((a, b) => a.name.localeCompare(b.name)); }
export function groupById(groups: Group[], id: number): Group | undefined { return groups.find((g) => g.id === id); }
export function groupName(groups: Group[], id: number): string { return groupById(groups, id)?.name ?? '—'; }
export function groupsLabel(ids: number[], groups: Group[]): string {
  if (!ids.length) return '—';
  return groupName(groups, ids[0]) + (ids.length > 1 ? ` + ${ids.length - 1}` : '');
}
export function groupsFull(ids: number[], groups: Group[]): string { return ids.map((id) => groupName(groups, id)).join(', '); }

const SECURITY_KEYS = ['sec_platform', 'sec_users', 'sec_roles'];
/** A group "can change settings" when it holds any non-view action on a Settings section or a Security page. */
export function grantsChangeSettings(g: Grants): boolean {
  return Object.entries(g).some(([k, acts]) => (k.startsWith('s:') || SECURITY_KEYS.includes(k)) && (acts ?? []).some((a) => a !== 'view'));
}
export function userChangesSettings(u: UserRow, groups: Group[]): boolean {
  return u.groups.some((id) => { const g = groupById(groups, id); return !!g && grantsChangeSettings(g.grants); });
}
export function groupManagesUsers(g: Group | undefined): boolean { return !!g && (g.grants.sec_users ?? []).includes('roles'); }
export function userManagesUsers(u: UserRow, groups: Group[]): boolean { return u.groups.some((id) => groupManagesUsers(groupById(groups, id))); }
/** The last ACTIVE person who can manage users & groups (S14 guard - the server enforces it with 409 last_admin). */
export function isLastManager(u: UserRow, users: UserRow[], groups: Group[]): boolean {
  if (u.status !== 'active' || !userManagesUsers(u, groups)) return false;
  return users.filter((x) => x.status === 'active' && userManagesUsers(x, groups)).length === 1;
}

export function requiredFor(u: UserRow, policy: Policy | null, groups: Group[]): boolean {
  if (!policy) return false;
  if (policy.mfaRequired === 'all') return true;
  if (policy.mfaRequired === 'settings') return userChangesSettings(u, groups);
  return false;
}
export function enforceShort(policy: Policy | null): string {
  if (!policy?.enforceOn) return 'soon';
  return fmtDate(policy.enforceOn, { month: 'short', day: 'numeric' });
}

export function Avatar({ u, lg }: { u: Pick<UserRow, 'firstName' | 'lastName' | 'name' | 'email' | 'mobile' | 'status'> & { photo?: string | null }; lg?: boolean }) {
  const hasName = !!((u.firstName ?? '').trim() || (u.lastName ?? '').trim());
  const pending = u.status === 'invited' || u.status === 'expired';
  return (
    <span className={`sc-av${lg ? ' lg' : ''}`} aria-hidden="true">
      {u.photo ? <img src={u.photo} alt="" className="sc-av-img" /> /* v12.10.9.3 (#1306) */
        : !hasName && pending ? <Ic k="user" /> : initials(u.firstName, u.lastName, u.name || u.email || u.mobile)}
    </span>
  );
}

/** The 2-step chip on a Users row (§21 vocabulary). */
export function SecondStepChip({ u, policy, groups, now }: { u: UserRow; policy: Policy | null; groups: Group[]; now: number }) {
  if (isInvite(u)) return <span className="sc-2fa none">Set up when they accept</span>;
  if (u.status === 'disabled') return <span className="sc-2fa none">—</span>;
  const m = u.methods ?? [];
  if (isLocked(u, now)) {
    return (
      <span className="sc-2fa locked" title={`Locked after ${policy?.lockAttempts ?? 5} wrong tries. 2-step: ${m.length ? m.map(methodName).join(', ') : 'none set up'}`}>
        <Ic k="lock" />Locked · {plural(lockMinsLeft(u, now), 'min')} left
      </span>
    );
  }
  if (m.length) {
    return (
      <span className="sc-2fa on" title={m.map(methodName).join(', ')}>
        <Ic k="shieldOk" />{methodName(m[0])}{m.length > 1 ? ` +${m.length - 1}` : ''}
      </span>
    );
  }
  if (requiredFor(u, policy, groups)) return <span className="sc-2fa need"><Ic k="warn" />Needed by {enforceShort(policy)}</span>;
  return <span className="sc-2fa none">No second step</span>;
}

/** Status tags (§21). */
export function StatusTags({ u }: { u: UserRow }) {
  return (
    <>
      {u.status === 'invited' && <span className="sc-st invited">Invite not accepted</span>}
      {u.status === 'expired' && <span className="sc-st expired">Invite expired</span>}
      {u.status === 'disabled' && <span className="sc-st disabled">Disabled</span>}
    </>
  );
}

/**
 * Multi-group picker - the same component in the person drawer and Invite.
 * Chips (first one "Shown first"), ↑ to show another first, × to remove (never the last one), and "+ Add a group…" with
 * every group that grants more than the viewer has disabled and labelled "— exceeds your access" (§36).
 */
export function GroupPicker({ value, groups, onChange, locked, ariaLabel = 'Add a group' }: {
  value: number[]; groups: Group[]; onChange: (ids: number[]) => void; locked?: boolean; ariaLabel?: string;
}) {
  const avail = sortGroups(groups).filter((g) => !value.includes(g.id));
  const first = groupById(groups, value[0]);
  return (
    <>
      <div className="sc-rp">
        {value.map((id, i) => {
          const g = groupById(groups, id);
          if (!g) return null;
          return (
            <span key={id} className={`sc-rchip${i === 0 ? ' first' : ''}${locked ? ' ro' : ''}`} title={g.description}>
              {i === 0 && value.length > 1 && <span className="sc-rchip-f">Shown first</span>}
              <b>{g.name}</b>
              {!locked && i > 0 && (
                <button type="button" className="sc-rchip-b" aria-label={`Show ${g.name} first`} title="Show first"
                  onClick={() => onChange([id, ...value.filter((x) => x !== id)])}><Ic k="up" /></button>
              )}
              {!locked && value.length > 1 && (
                <button type="button" className="sc-rchip-b" aria-label={`Remove ${g.name}`} onClick={() => onChange(value.filter((x) => x !== id))}><Ic k="x" /></button>
              )}
            </span>
          );
        })}
        {!locked && avail.length > 0 && (
          <select className="sc-rp-add" aria-label={ariaLabel} value="" onChange={(e) => { const id = Number(e.target.value); if (id) onChange([...value, id]); }}>
            <option value="">+ Add a group…</option>
            {avail.map((g) => (
              <option key={g.id} value={g.id} disabled={g.exceedsMe}>{g.name}{g.exceedsMe ? ' — exceeds your access' : ''}</option>
            ))}
          </select>
        )}
      </div>
      {value.length > 4 && <div className="sc-idst" style={{ marginTop: 6 }}>Many groups - a custom group might be simpler to manage.</div>}
      {value.length > 1
        ? <div className="sc-idst" style={{ marginTop: 6 }}>Together they get the most generous level from any of these groups.</div>
        : first ? <div className="sc-idst" style={{ marginTop: 6 }}>{first.description}</div> : null}
    </>
  );
}

/** Summary pill: "Group: Front desk +1" (§26). */
export function GroupPill({ ids, groups, onClick }: { ids: number[]; groups: Group[]; onClick?: () => void }) {
  if (!ids.length) return null;
  const inner = (
    <>
      <span className="rp-l">Group:</span>
      <span className="rp-n">{groupName(groups, ids[0])}</span>
      {ids.length > 1 && <span className="rp-c">+{ids.length - 1}</span>}
    </>
  );
  const all = groupsFull(ids, groups);
  return onClick
    ? <button type="button" className="sc-rolepill" title={`${all} — change in Access`} onClick={onClick}>{inner}</button>
    : <span className="sc-rolepill" title={all}>{inner}</span>;
}
